import { Component, useCallback, useEffect, useState } from "react";
import {
  Shield,
  Building2,
  Users as UsersIcon,
  Boxes,
  Plug,
  Activity,
  ScrollText,
  Plus,
  Search,
  X,
  CheckCircle2,
  AlertCircle,
  Inbox,
  Bot,
  Webhook
} from "lucide-react";
import adminApi from "../../services/adminService";
import { LoadingState, ErrorState } from "../ui/LoadingStates";

// A panel error must never white-screen the whole portal. Resets when the tab changes.
class PanelErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidUpdate(prev) {
    if (prev.tabKey !== this.props.tabKey && this.state.err) this.setState({ err: null });
  }
  render() {
    if (this.state.err) {
      return (
        <ErrorState message={"This view hit an error: " + (this.state.err.message || String(this.state.err))} />
      );
    }
    return this.props.children;
  }
}

/* ----------------------------- toasts + confirm + errors ----------------- */

// Extract a readable string from any API error (incl. FastAPI 422 detail arrays).
function errMsg(e, fallback = "Action failed") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  return e?.message || fallback;
}

let _toastSeq = 0;
const _toastListeners = new Set();
function toast(message, type = "success") {
  const t = { id: ++_toastSeq, message, type };
  _toastListeners.forEach((l) => l(t));
}
function Toaster() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const add = (t) => {
      setItems((xs) => [...xs, t]);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== t.id)), 3500);
    };
    _toastListeners.add(add);
    return () => _toastListeners.delete(add);
  }, []);
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
      {items.map((t) => (
        <div
          key={t.id}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm text-white ${
            t.type === "error" ? "bg-red-600" : "bg-gray-900"
          }`}
        >
          {t.type === "error" ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          {t.message}
        </div>
      ))}
    </div>
  );
}

let _confirmFn = null;
function confirmDialog(message) {
  return new Promise((resolve) => {
    if (_confirmFn) _confirmFn(message, resolve);
    else resolve(window.confirm(message));
  });
}
function ConfirmHost() {
  const [state, setState] = useState(null); // { message, resolve }
  useEffect(() => {
    _confirmFn = (message, resolve) => setState({ message, resolve });
    return () => {
      _confirmFn = null;
    };
  }, []);
  if (!state) return null;
  const done = (v) => {
    state.resolve(v);
    setState(null);
  };
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
      onMouseDown={() => done(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <p className="text-sm text-gray-800">{state.message}</p>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={() => done(false)} className="px-3 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-100">
            Cancel
          </button>
          <button
            onClick={() => done(true)}
            className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- small helpers ----------------------------- */

function useData(fetchFn, deps) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, data: null });
    fetchFn()
      .then((d) => alive && setState({ loading: false, error: null, data: d }))
      .catch((e) => {
        if (alive) setState({ loading: false, error: errMsg(e, "Request failed"), data: null });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

// Debounce a fast-changing value (e.g. a search box) before it hits the API.
function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const short = (id) => (id ? String(id).slice(0, 8) : "—");
const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");
const fmtNum = (v) => (typeof v === "number" ? v.toLocaleString() : v ?? "—");

const AUTH_COLORS = {
  mcp: "bg-purple-100 text-purple-700",
  api_key: "bg-blue-100 text-blue-700",
  oauth: "bg-amber-100 text-amber-700",
  webhook: "bg-green-100 text-green-700",
  no_auth: "bg-gray-100 text-gray-500"
};
const AuthChip = ({ t }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${AUTH_COLORS[t] || "bg-gray-100 text-gray-600"}`}>
    {t}
  </span>
);

const Badge = ({ ok, yes = "Yes", no = "No", danger = false }) => (
  <span
    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
      ok
        ? "bg-green-100 text-green-700"
        : danger
        ? "bg-red-100 text-red-700"
        : "bg-gray-100 text-gray-600"
    }`}
  >
    {ok ? yes : no}
  </span>
);

const ActBtn = ({ onClick, children, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
      danger ? "text-red-600 hover:bg-red-50" : "text-blue-600 hover:bg-blue-50"
    }`}
  >
    {children}
  </button>
);

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{fmtNum(value)}</div>
    </div>
  );
}

// Dependency-free donut via CSS conic-gradient + a white center hole.
function Donut({ segments, centerLabel, centerSub }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0) || 1;
  let acc = 0;
  const stops = segments.map((s) => {
    const start = (acc / total) * 360;
    acc += s.value || 0;
    const end = (acc / total) * 360;
    return `${s.color} ${start}deg ${end}deg`;
  });
  return (
    <div className="flex items-center gap-5">
      <div className="relative shrink-0" style={{ width: 104, height: 104 }}>
        <div style={{ width: 104, height: 104, borderRadius: "50%", background: `conic-gradient(${stops.join(",")})` }} />
        <div
          className="absolute inset-0 m-auto flex flex-col items-center justify-center"
          style={{ width: 70, height: 70, background: "#fff", borderRadius: "50%" }}
        >
          <div className="text-lg font-semibold text-gray-900">{centerLabel}</div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400">{centerSub}</div>
        </div>
      </div>
      <div className="space-y-1.5">
        {segments.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
            <span className="text-gray-700">{s.label}</span>
            <span className="text-gray-400">{fmtNum(s.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Horizontal bars: width ∝ request volume, split green(ok)/red(failed).
function UsageBars({ rows, nameKey = "provider_name" }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm text-gray-400 py-6 text-center">No usage in the window</div>;
  }
  const max = Math.max(...rows.map((r) => r.total_requests || 0), 1);
  return (
    <div className="space-y-2">
      {rows.slice(0, 10).map((r, i) => {
        const total = r.total_requests || 0;
        const ok = r.successful_requests || 0;
        const w = (total / max) * 100;
        const okPct = total ? (ok / total) * 100 : 0;
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-44 truncate text-sm text-gray-700" title={r[nameKey]}>
              {r[nameKey]}
            </div>
            <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
              <div className="h-full flex" style={{ width: `${w}%` }}>
                <div style={{ width: `${okPct}%`, background: "#16a34a" }} />
                <div style={{ width: `${100 - okPct}%`, background: "#dc2626" }} />
              </div>
            </div>
            <div className="w-32 text-right text-xs text-gray-500 tabular-nums">
              {fmtNum(total)} · {total ? Math.round((ok / total) * 100) : 0}% ok
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Status buckets: segmented quick-filter chips with live counts.
function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            value === o.key
              ? "bg-white text-blue-600 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          {o.label}
          {o.count != null && <span className="ml-1.5 text-xs text-gray-400">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}

const inputCls =
  "mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, onSubmit, submitLabel = "Save", busy = false, children }) {
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onMouseDown={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div className="space-y-3">{children}</div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? "Saving…" : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Table({ columns, rows, empty = "No records" }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Inbox className="w-8 h-8 mx-auto mb-2" />
        <div className="text-sm">{empty}</div>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-xl">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="px-4 py-2.5 font-medium whitespace-nowrap">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={row.id || i} className="hover:bg-gray-50">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-2.5 text-gray-700 whitespace-nowrap">
                  {c.render ? c.render(row) : String(row[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

async function runAction(fn, confirmMsg, onDone, successMsg = "Done") {
  if (confirmMsg && !(await confirmDialog(confirmMsg))) return;
  try {
    await fn();
    toast(successMsg, "success");
    onDone();
  } catch (e) {
    toast(errMsg(e), "error");
  }
}

/* ------------------------------- panels ---------------------------------- */

function CreateOrgModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    company_name: "",
    company_email: "",
    phone_number: "",
    industry: ""
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.company_name.trim()) {
      toast("Company name is required", "error");
      return;
    }
    setBusy(true);
    try {
      const body = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v && v.trim())
      );
      await adminApi.createOrganization(body);
      toast("Organization created");
      onCreated();
    } catch (e) {
      toast(errMsg(e), "error");
      setBusy(false);
    }
  };
  return (
    <Modal title="New Organization" onClose={onClose} onSubmit={submit} submitLabel="Create" busy={busy}>
      <Field label="Company name *">
        <input className={inputCls} value={form.company_name} onChange={set("company_name")} autoFocus />
      </Field>
      <Field label="Company email">
        <input className={inputCls} type="email" value={form.company_email} onChange={set("company_email")} />
      </Field>
      <Field label="Phone">
        <input className={inputCls} value={form.phone_number} onChange={set("phone_number")} />
      </Field>
      <Field label="Industry">
        <input className={inputCls} value={form.industry} onChange={set("industry")} />
      </Field>
    </Modal>
  );
}

function TenantsPanel() {
  const [refresh, setRefresh] = useState(0);
  const bump = () => setRefresh((x) => x + 1);
  const [bucket, setBucket] = useState("all"); // all | active | suspended
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [showCreate, setShowCreate] = useState(false);

  const isActive = bucket === "active" ? true : bucket === "suspended" ? false : undefined;
  const summary = useData(() => adminApi.orgSummary(), [refresh]);
  const { loading, error, data } = useData(
    () => adminApi.listOrganizations({ q: dq || undefined, is_active: isActive, limit: 200 }),
    [refresh, dq, bucket]
  );
  const s = summary.data || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total tenants" value={s.total} />
        <StatCard label="Active" value={s.active} />
        <StatCard label="Suspended" value={s.suspended} />
        <StatCard label="New (7d)" value={s.new_7d} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented
          value={bucket}
          onChange={setBucket}
          options={[
            { key: "all", label: "All", count: s.total },
            { key: "active", label: "Active", count: s.active },
            { key: "suspended", label: "Suspended", count: s.suspended }
          ]}
        />
        <div className="flex items-center gap-2">
          <SearchBox value={q} onChange={setQ} placeholder="Search tenants…" />
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> New Organization
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading tenants…" />
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <Table
          empty="No organizations match"
          rows={data}
          columns={[
            { key: "company_name", label: "Organization" },
            { key: "id", label: "ID", render: (r) => short(r.id) },
            {
              key: "is_active",
              label: "Status",
              render: (r) =>
                r.is_active ? <Badge ok yes="Active" /> : <Badge ok={false} danger no="Suspended" />
            },
            { key: "created_at", label: "Created", render: (r) => fmt(r.created_at) },
            {
              key: "actions",
              label: "",
              render: (r) =>
                r.is_active ? (
                  <ActBtn
                    danger
                    onClick={() =>
                      runAction(
                        () => adminApi.suspendOrganization(r.id),
                        `Suspend ${r.company_name}? Members lose access until reactivated.`,
                        bump
                      )
                    }
                  >
                    Suspend
                  </ActBtn>
                ) : (
                  <ActBtn
                    onClick={() => runAction(() => adminApi.reactivateOrganization(r.id), null, bump)}
                  >
                    Reactivate
                  </ActBtn>
                )
            }
          ]}
        />
      )}

      {showCreate && (
        <CreateOrgModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            bump();
          }}
        />
      )}
    </div>
  );
}

const USER_ROLES = ["admin", "developer", "analyst", "viewer"];

function CreateUserModal({ orgs, onClose, onCreated }) {
  const [form, setForm] = useState({
    organization_id: "",
    first_name: "",
    last_name: "",
    email: "",
    role: "viewer",
    auth_type: "password",
    password: "",
    send_setup_email: true,
    sso_provider: "google"
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.organization_id) return toast("Pick an organization", "error");
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim())
      return toast("First name, last name and email are required", "error");
    const invite = form.auth_type === "password" && form.send_setup_email;
    if (form.auth_type === "password" && !invite && form.password.length < 8)
      return toast("Password must be at least 8 characters", "error");
    setBusy(true);
    try {
      const body = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        role: form.role,
        auth_type: form.auth_type
      };
      if (form.auth_type === "password") {
        if (invite) body.send_setup_email = true;
        else body.password = form.password;
      } else {
        body.sso_provider = form.sso_provider;
      }
      const res = await adminApi.createUser(form.organization_id, body);
      if (invite && res && res.invite_sent === false)
        toast("User created, but the invite email failed — use “Resend setup”.", "error");
      else if (invite) toast(`User created — invite sent to ${body.email}`);
      else toast("User created");
      onCreated();
    } catch (e) {
      toast(errMsg(e), "error");
      setBusy(false);
    }
  };
  return (
    <Modal title="New User" onClose={onClose} onSubmit={submit} submitLabel="Create" busy={busy}>
      <Field label="Organization *">
        <select className={inputCls} value={form.organization_id} onChange={set("organization_id")}>
          <option value="">Select an organization…</option>
          {(orgs || []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.company_name}
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name *">
          <input className={inputCls} value={form.first_name} onChange={set("first_name")} />
        </Field>
        <Field label="Last name *">
          <input className={inputCls} value={form.last_name} onChange={set("last_name")} />
        </Field>
      </div>
      <Field label="Email *">
        <input className={inputCls} type="email" value={form.email} onChange={set("email")} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Role">
          <select className={inputCls} value={form.role} onChange={set("role")}>
            {USER_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Auth type">
          <select className={inputCls} value={form.auth_type} onChange={set("auth_type")}>
            <option value="password">password</option>
            <option value="sso">sso</option>
          </select>
        </Field>
      </div>
      {form.auth_type === "password" ? (
        <>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.send_setup_email}
              onChange={(e) =>
                setForm((f) => ({ ...f, send_setup_email: e.target.checked }))
              }
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            Email a set-password link (recommended)
          </label>
          {form.send_setup_email ? (
            <p className="text-xs text-gray-500">
              The user receives an email to set their own password — no password needed here.
            </p>
          ) : (
            <Field label="Password * (min 8)">
              <input className={inputCls} type="password" value={form.password} onChange={set("password")} />
            </Field>
          )}
        </>
      ) : (
        <Field label="SSO provider">
          <select className={inputCls} value={form.sso_provider} onChange={set("sso_provider")}>
            <option value="google">google</option>
            <option value="microsoft">microsoft</option>
          </select>
        </Field>
      )}
    </Modal>
  );
}

function UsersPanel() {
  const [refresh, setRefresh] = useState(0);
  const bump = () => setRefresh((x) => x + 1);
  const resendSetup = async (r) => {
    if (!(await confirmDialog(`Resend setup email to ${r.email}?`))) return;
    try {
      const res = await adminApi.sendSetupEmail(r.id);
      if (res && res.invite_sent === false)
        toast("Email failed to send — check Gmail delegation.", "error");
      else toast(`Setup email sent to ${r.email}`);
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };
  const [bucket, setBucket] = useState("all"); // all | active | inactive | admins
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [role, setRole] = useState("");
  const [orgId, setOrgId] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const orgs = useData(() => adminApi.listOrganizations({ limit: 200 }), []);
  const summary = useData(() => adminApi.usersSummary(), [refresh]);

  const params = { q: dq || undefined, role: role || undefined, organization_id: orgId || undefined, limit: 200 };
  if (bucket === "active") params.is_active = true;
  if (bucket === "inactive") params.is_active = false;
  if (bucket === "admins") params.is_platform_admin = true;

  const { loading, error, data } = useData(() => adminApi.listUsers(params), [
    refresh,
    dq,
    role,
    orgId,
    bucket
  ]);
  const s = summary.data || {};
  const orgName = (id) => orgs.data?.find((o) => o.id === id)?.company_name || short(id);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total users" value={s.total} />
        <StatCard label="Active" value={s.active} />
        <StatCard label="Inactive" value={s.inactive} />
        <StatCard label="Platform admins" value={s.platform_admins} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented
          value={bucket}
          onChange={setBucket}
          options={[
            { key: "all", label: "All", count: s.total },
            { key: "active", label: "Active", count: s.active },
            { key: "inactive", label: "Inactive", count: s.inactive },
            { key: "admins", label: "Admins", count: s.platform_admins }
          ]}
        />
        <div className="flex flex-wrap items-center gap-2">
          <SearchBox value={q} onChange={setQ} placeholder="Search users…" />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All roles</option>
            {USER_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 max-w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All orgs</option>
            {(orgs.data || []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.company_name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> New User
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading users…" />
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <Table
          empty="No users match"
          rows={data}
          columns={[
            { key: "email", label: "Email" },
            { key: "role", label: "Role" },
            { key: "organization_id", label: "Org", render: (r) => orgName(r.organization_id) },
            { key: "is_active", label: "Active", render: (r) => <Badge ok={r.is_active} /> },
            {
              key: "is_platform_admin",
              label: "Platform admin",
              render: (r) => <Badge ok={r.is_platform_admin} />
            },
            {
              key: "actions",
              label: "",
              render: (r) => (
                <div className="flex gap-1">
                  {r.is_active ? (
                    <ActBtn
                      danger
                      onClick={() =>
                        runAction(() => adminApi.deactivateUser(r.id), `Deactivate ${r.email}?`, bump)
                      }
                    >
                      Deactivate
                    </ActBtn>
                  ) : (
                    <ActBtn onClick={() => runAction(() => adminApi.reactivateUser(r.id), null, bump)}>
                      Reactivate
                    </ActBtn>
                  )}
                  {r.auth_type === "password" && (
                    <ActBtn onClick={() => resendSetup(r)}>Resend setup</ActBtn>
                  )}
                  {r.is_platform_admin ? (
                    <ActBtn
                      danger
                      onClick={() =>
                        runAction(
                          () => adminApi.revokePlatformAdmin(r.id),
                          `Revoke platform-admin from ${r.email}?`,
                          bump
                        )
                      }
                    >
                      Revoke admin
                    </ActBtn>
                  ) : (
                    <ActBtn
                      onClick={() =>
                        runAction(
                          () => adminApi.grantPlatformAdmin(r.id),
                          `Grant platform-admin to ${r.email}?`,
                          bump
                        )
                      }
                    >
                      Make admin
                    </ActBtn>
                  )}
                </div>
              )
            }
          ]}
        />
      )}

      {showCreate && (
        <CreateUserModal
          orgs={orgs.data}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            bump();
          }}
        />
      )}
    </div>
  );
}

const PROVIDER_TYPES = [
  "business_intelligence",
  "communication",
  "crm",
  "property_data",
  "risk_intelligence",
  "weather_intelligence",
  "in_house_integrations",
  "nativeorange"
];
const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];
const CATALOG_LABEL = { provider: "Provider", api: "Provider API", endpoint: "Endpoint" };

function CatalogFormModal({ kind, row, providers, apis, onClose, onSaved }) {
  const editing = !!row;
  const initial = () => {
    if (kind === "provider")
      return {
        provider_name: row?.provider_name || "",
        provider_type: row?.provider_type || "business_intelligence",
        ui_name: row?.ui_name || "",
        description: row?.description || "",
        documentation_url: row?.documentation_url || "",
        logo_slug: row?.logo_slug || "",
        is_active: row ? !!row.is_active : true
      };
    if (kind === "api")
      return {
        provider_id: row?.provider_id || "",
        name: row?.name || "",
        base_url: row?.base_url || "",
        version: row?.version || "",
        description: row?.description || "",
        documentation_url: row?.documentation_url || "",
        is_active: row ? !!row.is_active : true
      };
    return {
      provider_api_id: row?.provider_api_id || "",
      name: row?.name || "",
      http_method: row?.http_method || "GET",
      path: row?.path || "",
      description: row?.description || "",
      is_active: row ? !!row.is_active : true
    };
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const addOpt = (body, keys) =>
    keys.forEach((k) => {
      if (form[k] && String(form[k]).trim()) body[k] = String(form[k]).trim();
    });

  const submit = async () => {
    setBusy(true);
    try {
      if (kind === "provider") {
        if (!form.provider_name.trim()) throw new Error("Name is required");
        const body = {
          provider_name: form.provider_name.trim(),
          provider_type: form.provider_type,
          is_active: form.is_active
        };
        addOpt(body, ["ui_name", "description", "documentation_url", "logo_slug"]);
        editing ? await adminApi.updateProvider(row.id, body) : await adminApi.createProvider(body);
      } else if (kind === "api") {
        if (!form.provider_id) throw new Error("Provider is required");
        if (!form.name.trim() || !form.base_url.trim()) throw new Error("Name and base URL are required");
        const body = {
          provider_id: form.provider_id,
          name: form.name.trim(),
          base_url: form.base_url.trim(),
          is_active: form.is_active
        };
        addOpt(body, ["version", "description", "documentation_url"]);
        editing ? await adminApi.updateProviderApi(row.id, body) : await adminApi.createProviderApi(body);
      } else {
        if (!form.provider_api_id) throw new Error("Provider API is required");
        if (!form.name.trim() || !form.path.trim()) throw new Error("Name and path are required");
        const body = {
          provider_api_id: form.provider_api_id,
          name: form.name.trim(),
          http_method: form.http_method,
          path: form.path.trim(),
          is_active: form.is_active
        };
        addOpt(body, ["description"]);
        editing ? await adminApi.updateEndpoint(row.id, body) : await adminApi.createEndpoint(body);
      }
      toast(editing ? CATALOG_LABEL[kind] + " updated" : CATALOG_LABEL[kind] + " created");
      onSaved();
    } catch (e) {
      toast(errMsg(e), "error");
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`${editing ? "Edit" : "New"} ${CATALOG_LABEL[kind]}`}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={editing ? "Save" : "Create"}
      busy={busy}
    >
      {kind === "provider" && (
        <>
          <Field label="Name *">
            <input className={inputCls} value={form.provider_name} onChange={set("provider_name")} autoFocus />
          </Field>
          <Field label="Type *">
            <select className={inputCls} value={form.provider_type} onChange={set("provider_type")}>
              {PROVIDER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Display name">
            <input className={inputCls} value={form.ui_name} onChange={set("ui_name")} />
          </Field>
          <Field label="Description">
            <input className={inputCls} value={form.description} onChange={set("description")} />
          </Field>
          <Field label="Docs URL">
            <input className={inputCls} value={form.documentation_url} onChange={set("documentation_url")} />
          </Field>
          <Field label="Logo slug">
            <input className={inputCls} value={form.logo_slug} onChange={set("logo_slug")} />
          </Field>
        </>
      )}
      {kind === "api" && (
        <>
          <Field label="Provider *">
            <select className={inputCls} value={form.provider_id} onChange={set("provider_id")}>
              <option value="">Select a provider…</option>
              {(providers || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.provider_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Name *">
            <input className={inputCls} value={form.name} onChange={set("name")} />
          </Field>
          <Field label="Base URL *">
            <input className={inputCls} value={form.base_url} onChange={set("base_url")} placeholder="https://api.example.com" />
          </Field>
          <Field label="Version">
            <input className={inputCls} value={form.version} onChange={set("version")} placeholder="v1" />
          </Field>
          <Field label="Description">
            <input className={inputCls} value={form.description} onChange={set("description")} />
          </Field>
          <Field label="Docs URL">
            <input className={inputCls} value={form.documentation_url} onChange={set("documentation_url")} />
          </Field>
        </>
      )}
      {kind === "endpoint" && (
        <>
          <Field label="Provider API *">
            <select className={inputCls} value={form.provider_api_id} onChange={set("provider_api_id")}>
              <option value="">Select an API…</option>
              {(apis || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Name *">
            <input className={inputCls} value={form.name} onChange={set("name")} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Method *">
              <select className={inputCls} value={form.http_method} onChange={set("http_method")}>
                {HTTP_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Path *">
              <input className={inputCls} value={form.path} onChange={set("path")} placeholder="/v1/things/{id}" />
            </Field>
          </div>
          <Field label="Description">
            <input className={inputCls} value={form.description} onChange={set("description")} />
          </Field>
        </>
      )}
      <label className="flex items-center gap-2 text-sm text-gray-700 pt-1">
        <input type="checkbox" checked={form.is_active} onChange={set("is_active")} /> Active
      </label>
    </Modal>
  );
}

function CatalogPanel() {
  const [sub, setSub] = useState("providers");
  const [refresh, setRefresh] = useState(0);
  const bump = () => setRefresh((x) => x + 1);
  const [modal, setModal] = useState(null); // { kind, row? }

  const providers = useData(() => adminApi.listProviders({ limit: 200 }), [refresh]);
  const apis = useData(() => adminApi.listProviderApis({ limit: 200 }), [refresh]);
  const endpoints = useData(() => adminApi.listEndpoints({ limit: 200 }), [refresh]);

  const current = sub === "providers" ? providers : sub === "apis" ? apis : endpoints;
  const kindOf = sub === "providers" ? "provider" : sub === "apis" ? "api" : "endpoint";
  const deact = {
    provider: adminApi.deactivateProvider,
    api: adminApi.deactivateProviderApi,
    endpoint: adminApi.deactivateEndpoint
  }[kindOf];
  const providerName = (id) => providers.data?.find((p) => p.id === id)?.provider_name || short(id);
  const apiName = (id) => apis.data?.find((a) => a.id === id)?.name || short(id);

  const actionsCol = {
    key: "actions",
    label: "",
    render: (r) => (
      <div className="flex gap-1">
        <ActBtn onClick={() => setModal({ kind: kindOf, row: r })}>Edit</ActBtn>
        {r.is_active && (
          <ActBtn
            danger
            onClick={() =>
              runAction(() => deact(r.id), `Deactivate ${r.name || r.provider_name}?`, bump)
            }
          >
            Deactivate
          </ActBtn>
        )}
      </div>
    )
  };

  const cols = {
    providers: [
      { key: "provider_name", label: "Name" },
      { key: "provider_type", label: "Type" },
      {
        key: "organization_id",
        label: "Scope",
        render: (r) => (r.organization_id ? `org ${short(r.organization_id)}` : "global")
      },
      { key: "is_active", label: "Active", render: (r) => <Badge ok={r.is_active} /> },
      actionsCol
    ],
    apis: [
      { key: "name", label: "API" },
      { key: "provider_id", label: "Provider", render: (r) => providerName(r.provider_id) },
      { key: "base_url", label: "Base URL" },
      { key: "is_active", label: "Active", render: (r) => <Badge ok={r.is_active} /> },
      actionsCol
    ],
    endpoints: [
      { key: "name", label: "Endpoint" },
      { key: "provider_api_id", label: "API", render: (r) => apiName(r.provider_api_id) },
      { key: "http_method", label: "Method" },
      { key: "path", label: "Path" },
      { key: "is_active", label: "Active", render: (r) => <Badge ok={r.is_active} /> },
      actionsCol
    ]
  };

  const subTabs = [
    ["providers", "Providers"],
    ["apis", "APIs"],
    ["endpoints", "Endpoints"]
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {subTabs.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setSub(k)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                sub === k ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: kindOf })}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New {CATALOG_LABEL[kindOf]}
        </button>
      </div>
      {current.loading ? (
        <LoadingState message="Loading catalog…" />
      ) : current.error ? (
        <ErrorState message={current.error} />
      ) : (
        <Table empty="No records" rows={current.data} columns={cols[sub]} />
      )}
      {modal && (
        <CatalogFormModal
          kind={modal.kind}
          row={modal.row}
          providers={providers.data}
          apis={apis.data}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            bump();
          }}
        />
      )}
    </div>
  );
}

const AUTH_TYPES = ["no_auth", "api_key", "mcp", "oauth", "webhook"];

function IntegrationsPanel() {
  const integrations = useData(() => adminApi.listIntegrations({ limit: 200 }), []);
  const orgs = useData(() => adminApi.listOrganizations({ limit: 200 }), []);
  const providers = useData(() => adminApi.listProviders({ limit: 200 }), []);
  const apis = useData(() => adminApi.listProviderApis({ limit: 200 }), []);

  const [bucket, setBucket] = useState("all"); // all | enabled | disabled
  const [auth, setAuth] = useState("");
  const [orgId, setOrgId] = useState("");
  const [q, setQ] = useState("");
  const dq = useDebounced(q);

  const orgName = (id) => orgs.data?.find((o) => o.id === id)?.company_name || short(id);
  const provName = (id) => providers.data?.find((p) => p.id === id)?.provider_name || short(id);
  const apiName = (id) => apis.data?.find((a) => a.id === id)?.name || short(id);

  if (integrations.loading) return <LoadingState message="Loading integrations…" />;
  if (integrations.error) return <ErrorState message={integrations.error} />;

  const all = integrations.data || [];
  const counts = {
    total: all.length,
    configured: all.filter((i) => i.secret_configured).length,
    enabled: all.filter((i) => i.is_enabled).length
  };
  counts.disabled = counts.total - counts.enabled;

  let rows = all;
  if (bucket === "enabled") rows = rows.filter((i) => i.is_enabled);
  if (bucket === "disabled") rows = rows.filter((i) => !i.is_enabled);
  if (auth) rows = rows.filter((i) => i.auth_type === auth);
  if (orgId) rows = rows.filter((i) => i.organization_id === orgId);
  if (dq) {
    const s = dq.toLowerCase();
    rows = rows.filter(
      (i) =>
        orgName(i.organization_id).toLowerCase().includes(s) ||
        provName(i.provider_id).toLowerCase().includes(s) ||
        apiName(i.provider_api_id).toLowerCase().includes(s)
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total integrations" value={counts.total} />
        <StatCard label="Secret configured" value={counts.configured} />
        <StatCard label="Enabled" value={counts.enabled} />
        <StatCard label="Disabled" value={counts.disabled} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented
          value={bucket}
          onChange={setBucket}
          options={[
            { key: "all", label: "All", count: counts.total },
            { key: "enabled", label: "Enabled", count: counts.enabled },
            { key: "disabled", label: "Disabled", count: counts.disabled }
          ]}
        />
        <div className="flex flex-wrap items-center gap-2">
          <SearchBox value={q} onChange={setQ} placeholder="Search org / provider / API…" />
          <select
            value={auth}
            onChange={(e) => setAuth(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All auth</option>
            {AUTH_TYPES.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 max-w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All orgs</option>
            {(orgs.data || []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.company_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Table
        empty="No integrations match"
        rows={rows}
        columns={[
          { key: "organization_id", label: "Org", render: (r) => orgName(r.organization_id) },
          { key: "provider_id", label: "Provider", render: (r) => provName(r.provider_id) },
          { key: "provider_api_id", label: "API", render: (r) => apiName(r.provider_api_id) },
          { key: "auth_type", label: "Auth", render: (r) => <AuthChip t={r.auth_type} /> },
          {
            key: "secret_configured",
            label: "Secret",
            render: (r) =>
              r.secret_configured ? <Badge ok yes="Configured" /> : <Badge ok={false} no="None" />
          },
          { key: "is_enabled", label: "Enabled", render: (r) => <Badge ok={r.is_enabled} /> },
          {
            key: "base_url",
            label: "Base URL override",
            render: (r) =>
              r.base_url ? (
                <span className="block max-w-[260px] truncate" title={r.base_url}>
                  {r.base_url}
                </span>
              ) : (
                "—"
              )
          }
        ]}
      />
    </div>
  );
}

function ObservabilityPanel() {
  const [providerId, setProviderId] = useState("");
  const [orgId, setOrgId] = useState("");
  const scoped = providerId || undefined;
  const org = orgId || undefined;
  const providers = useData(() => adminApi.listProviders({ limit: 200 }), []);
  const orgs = useData(() => adminApi.listOrganizations({ limit: 200 }), []);
  const summary = useData(
    () => adminApi.executionsSummary({ days: 7, provider_id: scoped, organization_id: org }),
    [providerId, orgId]
  );
  const usageProv = useData(() => adminApi.usageProviders({ days: 7, organization_id: org }), [orgId]);
  const usageApi = useData(
    () => adminApi.usageProviderApis({ days: 7, provider_id: scoped, organization_id: org }),
    [providerId, orgId]
  );
  const execs = useData(
    () => adminApi.executions({ days: 7, provider_id: scoped, organization_id: org, limit: 25 }),
    [providerId, orgId]
  );
  const s = summary.data || {};
  const scopeLabel = orgId
    ? "· " + (orgs.data?.find((o) => o.id === orgId)?.company_name || "selected tenant")
    : "· all tenants";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-700">
          API executions — last 7 days {scopeLabel}
          {providerId ? " · selected provider" : ""}
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 max-w-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All tenants</option>
            {(orgs.data || []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.company_name}
              </option>
            ))}
          </select>
          <select
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 max-w-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All providers</option>
            {(providers.data || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.provider_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {summary.loading ? (
        <LoadingState message="Loading summary…" size="small" />
      ) : summary.error ? (
        <ErrorState message={summary.error} />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Total" value={s.total} />
          <StatCard label="Succeeded" value={s.succeeded} />
          <StatCard label="Failed" value={s.failed} />
          <StatCard label="Running" value={s.running} />
          <StatCard label="p50 ms" value={s.p50_latency_ms} />
          <StatCard label="p95 ms" value={s.p95_latency_ms} />
        </div>
      )}

      {!summary.loading && !summary.error && (s.total || 0) > 0 && (
        <div className="rounded-2xl border border-gray-200 p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-3">Outcome split (7d)</div>
          <Donut
            centerLabel={`${Math.round(((s.succeeded || 0) / (s.total || 1)) * 100)}%`}
            centerSub="success"
            segments={[
              { label: "Succeeded", value: s.succeeded || 0, color: "#16a34a" },
              { label: "Failed", value: s.failed || 0, color: "#dc2626" },
              { label: "Running", value: s.running || 0, color: "#d97706" }
            ]}
          />
        </div>
      )}

      {!providerId && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Usage by provider (7d)</h3>
          {usageProv.loading ? (
            <LoadingState message="Loading usage…" size="small" />
          ) : usageProv.error ? (
            <ErrorState message={usageProv.error} />
          ) : (
            <UsageBars rows={usageProv.data} />
          )}
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Usage by API {providerId ? "· selected provider" : "(7d)"}
        </h3>
        {usageApi.loading ? (
          <LoadingState message="Loading API usage…" size="small" />
        ) : usageApi.error ? (
          <ErrorState message={usageApi.error} />
        ) : (
          <Table
            empty="No API usage in the window"
            rows={usageApi.data}
            columns={[
              { key: "provider_api_name", label: "API" },
              { key: "total_requests", label: "Requests" },
              { key: "successful_requests", label: "OK" },
              { key: "failed_requests", label: "Failed" },
              { key: "avg_latency_ms", label: "Avg ms" }
            ]}
          />
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Recent executions {providerId ? "· selected provider" : ""}
        </h3>
        {execs.loading ? (
          <LoadingState message="Loading executions…" size="small" />
        ) : execs.error ? (
          <ErrorState message={execs.error} />
        ) : (
          <Table
            empty="No executions in the window"
            rows={execs.data}
            columns={[
              { key: "started_at", label: "When", render: (r) => fmt(r.started_at) },
              { key: "organization_id", label: "Org", render: (r) => short(r.organization_id) },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "success" ? (
                    <Badge ok yes="success" />
                  ) : r.status === "running" ? (
                    <Badge ok={false} no="running" />
                  ) : (
                    <Badge ok={false} danger no={r.status} />
                  )
              },
              { key: "http_status_code", label: "HTTP" },
              { key: "duration_ms", label: "ms" },
              {
                key: "error_message",
                label: "Error",
                render: (r) => (r.error_message ? String(r.error_message).slice(0, 60) : "—")
              }
            ]}
          />
        )}
      </div>
    </div>
  );
}

function AuditPanel() {
  const { loading, error, data } = useData(() => adminApi.auditLog({ limit: 100 }), []);
  if (loading) return <LoadingState message="Loading audit log…" />;
  if (error) return <ErrorState message={error} />;
  return (
    <Table
      empty="No audit entries yet"
      rows={data}
      columns={[
        { key: "created_at", label: "When", render: (r) => fmt(r.created_at) },
        { key: "actor_email", label: "Actor" },
        { key: "action", label: "Action" },
        { key: "target_type", label: "Target" },
        { key: "target_id", label: "Target ID", render: (r) => short(r.target_id) },
        { key: "summary", label: "Summary", render: (r) => r.summary || "—" }
      ]}
    />
  );
}

function AgentRunsPanel() {
  const [orgId, setOrgId] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const org = orgId || undefined;
  const orgs = useData(() => adminApi.listOrganizations({ limit: 200 }), []);
  const summary = useData(() => adminApi.agentRunsSummary({ days: 7, organization_id: org }), [orgId]);
  const runs = useData(
    () => adminApi.agentRuns({ days: 7, organization_id: org, errors_only: errorsOnly, limit: 100 }),
    [orgId, errorsOnly]
  );
  const s = summary.data || {};
  const orgName = (id) => orgs.data?.find((o) => o.id === id)?.company_name || short(id);
  const toolNames = (tools) => (tools || []).map((t) => t.tool).filter(Boolean).join(", ");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-700">
          Ask Orbit agent runs — last 7 days {orgId ? "· " + orgName(orgId) : "· all tenants"}
        </h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />
            Errors only
          </label>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 max-w-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All tenants</option>
            {(orgs.data || []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.company_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {summary.loading ? (
        <LoadingState message="Loading summary…" size="small" />
      ) : summary.error ? (
        <ErrorState message={summary.error} />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Runs" value={s.total_runs} />
          <StatCard label="Avg ms" value={s.avg_duration_ms} />
          <StatCard label="p95 ms" value={s.p95_duration_ms} />
          <StatCard label="Error runs" value={s.error_runs} />
          <StatCard label="No-tool answers" value={s.no_tool_runs} />
          <StatCard label="Tool calls" value={s.total_tool_calls} />
        </div>
      )}

      {runs.loading ? (
        <LoadingState message="Loading agent runs…" />
      ) : runs.error ? (
        <ErrorState message={runs.error} />
      ) : (
        <Table
          empty="No agent runs in the window yet"
          rows={runs.data}
          columns={[
            { key: "created_at", label: "When", render: (r) => fmt(r.created_at) },
            { key: "organization_id", label: "Org", render: (r) => orgName(r.organization_id) },
            { key: "user_email", label: "User", render: (r) => r.user_email || "—" },
            { key: "duration_ms", label: "ms", render: (r) => fmtNum(r.duration_ms) },
            { key: "tool_count", label: "Tools", render: (r) => r.tool_count },
            {
              key: "tool_errors",
              label: "Errors",
              render: (r) =>
                r.tool_errors > 0 ? <Badge ok={false} danger no={r.tool_errors} /> : <span className="text-gray-400">0</span>
            },
            {
              key: "no_tool_answer",
              label: "No-tool",
              render: (r) =>
                r.no_tool_answer ? <Badge ok={false} danger no="yes" /> : <span className="text-gray-400">—</span>
            },
            {
              key: "tools",
              label: "Tools used",
              render: (r) => {
                const names = toolNames(r.tools);
                return names ? (
                  <span className="block max-w-[280px] truncate" title={names}>
                    {names}
                  </span>
                ) : (
                  "—"
                );
              }
            }
          ]}
        />
      )}
    </div>
  );
}

function EventsPanel() {
  const [orgId, setOrgId] = useState("");
  const org = orgId || undefined;
  const orgs = useData(() => adminApi.listOrganizations({ limit: 200 }), []);
  const summary = useData(() => adminApi.eventsSummary({ days: 7, organization_id: org }), [orgId]);
  const topApps = useData(() => adminApi.eventsTopApps({ days: 7, organization_id: org, limit: 12 }), [orgId]);
  const recent = useData(() => adminApi.events({ days: 7, organization_id: org, limit: 60 }), [orgId]);
  const orgName = (id) => orgs.data?.find((o) => o.id === id)?.company_name || short(id);

  // dispatch success% per event_type, derived from summary.dispatch
  const disp = {};
  (summary.data?.dispatch || []).forEach((d) => {
    const t = disp[d.event_type] || (disp[d.event_type] = { ok: 0, total: 0 });
    t.total += d.n;
    if (d.success === "true") t.ok += d.n;
  });
  const okPct = (et) => {
    const t = disp[et];
    return t && t.total ? Math.round((t.ok / t.total) * 100) : null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-700">
          Webhook events — last 7 days {orgId ? "· " + orgName(orgId) : "· all tenants"}
        </h3>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 max-w-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All tenants</option>
          {(orgs.data || []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.company_name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Events by type</h3>
        {summary.loading ? (
          <LoadingState message="Loading…" size="small" />
        ) : summary.error ? (
          <ErrorState message={summary.error} />
        ) : (
          <Table
            empty="No events in the window"
            rows={summary.data?.by_type}
            columns={[
              { key: "event_type", label: "Event" },
              { key: "events", label: "Events", render: (r) => fmtNum(r.events) },
              { key: "apps", label: "Apps", render: (r) => fmtNum(r.apps) },
              {
                key: "per_app",
                label: "Per app",
                render: (r) =>
                  Number(r.per_app) > 2 ? (
                    <Badge ok={false} danger no={r.per_app + "×"} />
                  ) : (
                    <span className="text-gray-600">{r.per_app}×</span>
                  )
              },
              {
                key: "ok",
                label: "Dispatch OK",
                render: (r) => {
                  const p = okPct(r.event_type);
                  return p == null ? (
                    "—"
                  ) : p < 100 ? (
                    <Badge ok={false} danger no={p + "%"} />
                  ) : (
                    <Badge ok yes={p + "%"} />
                  );
                }
              },
              { key: "last_at", label: "Last" }
            ]}
          />
        )}
        <p className="text-xs text-gray-400 mt-2">
          "Per app" &gt; ~1 means the same application is notified repeatedly (burst). 1.0 is healthy.
        </p>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Top applications by notification count (burst detector)</h3>
        {topApps.loading ? (
          <LoadingState message="Loading…" size="small" />
        ) : topApps.error ? (
          <ErrorState message={topApps.error} />
        ) : (
          <Table
            empty="No data"
            rows={topApps.data}
            columns={[
              { key: "application_id", label: "Application" },
              { key: "event_type", label: "Event" },
              {
                key: "n",
                label: "Notifications",
                render: (r) => (r.n > 5 ? <Badge ok={false} danger no={fmtNum(r.n)} /> : <span>{fmtNum(r.n)}</span>)
              },
              { key: "span", label: "Window", render: (r) => `${r.first_at} → ${r.last_at}` }
            ]}
          />
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent events</h3>
        {recent.loading ? (
          <LoadingState message="Loading…" size="small" />
        ) : recent.error ? (
          <ErrorState message={recent.error} />
        ) : (
          <Table
            empty="No events"
            rows={recent.data}
            columns={[
              { key: "received_at", label: "When" },
              { key: "organization_id", label: "Org", render: (r) => orgName(r.organization_id) },
              { key: "application_id", label: "Application", render: (r) => r.application_id || "—" },
              { key: "event_type", label: "Event" },
              { key: "fanout", label: "→ hooks" },
              {
                key: "ok",
                label: "Dispatch",
                render: (r) =>
                  r.ok === "true" ? (
                    <Badge ok yes={r.status_code || "ok"} />
                  ) : (
                    <Badge ok={false} danger no={r.status_code || "fail"} />
                  )
              },
              { key: "webhook", label: "Webhook", render: (r) => r.webhook || "—" },
              { key: "duration_ms", label: "ms", render: (r) => r.duration_ms || "—" }
            ]}
          />
        )}
      </div>
    </div>
  );
}

/* ------------------------------- shell ----------------------------------- */

const TABS = [
  ["tenants", "Tenants", Building2, TenantsPanel],
  ["users", "Users", UsersIcon, UsersPanel],
  ["catalog", "Catalog", Boxes, CatalogPanel],
  ["integrations", "Integrations", Plug, IntegrationsPanel],
  ["observability", "Observability", Activity, ObservabilityPanel],
  ["agent-runs", "Agent Runs", Bot, AgentRunsPanel],
  ["events", "Events", Webhook, EventsPanel],
  ["audit", "Audit log", ScrollText, AuditPanel]
];

export default function AdminPortal() {
  const [tab, setTab] = useState("tenants");
  const Active = TABS.find(([k]) => k === tab)?.[3] || TenantsPanel;

  return (
    <div className="flex-1 overflow-y-auto">
      <Toaster />
      <ConfirmHost />
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-1">
          <Shield className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Admin Portal</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Platform-operator console — manage tenants, users, the global catalog,
          integrations, and cross-tenant observability.
        </p>

        <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-6">
          {TABS.map(([k, label, Icon]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === k
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        <PanelErrorBoundary tabKey={tab}>
          <Active />
        </PanelErrorBoundary>
      </div>
    </div>
  );
}
